/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import React, { useMemo, useLayoutEffect, useEffect, useState } from "react";
import objectPath from "object-path";
import { useLocation } from "react-router-dom";
import { QuickActions } from "./components/QuickActions";
import { BreadCrumbs } from "./components/BreadCrumbs";
import {
  getBreadcrumbsAndTitle,
  useSubheader,
} from "../../_core/MetronicSubheader";
import { useHtmlClassService } from "../../_core/MetronicLayout";
import { baseUrl, config } from "../../../../services/config";
import NavbarSlider from "./NavbarSlider";
import { PremissionChecker } from "../../../../services/PremissionChecker";
import { useSelector } from "react-redux";
import "./subheader-enhancements.css";

export function SubHeader() {
  const user = useSelector((state) => state.user.user);
  const authToken = localStorage.getItem("userToken");
  const [monthlyMessage, setMonthlyMessage] = useState("");
  const [annualMessage, setAnnualMessage] = useState("");

  const uiService = useHtmlClassService();
  const location = useLocation();
  const subheader = useSubheader();

  const layoutProps = useMemo(() => {
    return {
      config: uiService.config,
      subheaderMobileToggle: objectPath.get(
        uiService.config,
        "subheader.mobile-toggle",
      ),
      subheaderCssClasses: uiService.getClasses("subheader", true),
      subheaderContainerCssClasses: uiService.getClasses(
        "subheader_container",
        true,
      ),
    };
  }, [uiService]);

  useLayoutEffect(() => {
    const aside = getBreadcrumbsAndTitle("kt_aside_menu", location.pathname);
    const header = getBreadcrumbsAndTitle("kt_header_menu", location.pathname);
    const breadcrumbs =
      aside && aside.breadcrumbs.length > 0
        ? aside.breadcrumbs
        : header.breadcrumbs;
    subheader.setBreadcrumbs(breadcrumbs);
    subheader.setTitle(
      aside && aside.title && aside.title.length > 0
        ? aside.title
        : header.title,
    );
    // eslint-disable-next-line
  }, [location.pathname]);

  // Do not remove this useEffect, need from update title/breadcrumbs outside (from the page)
  useEffect(() => {}, [subheader]);

  useEffect(() => {
    getSettingData();
  }, []);

  const getSettingData = async () => {
    await fetch(`${baseUrl}/get-settings`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        getCustomersData(data.payload?.settings?.unit_id);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getCustomersData = (id) => {
    if (id === null) return;

    fetch(`https://portal.tvoxel.com/api/customers/${id}/invoices`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const invoices = data.data;

        // Helper to format + sort + deduplicate months
        const formatMonths = (invoiceList) => {
          if (!invoiceList.length) return "";

          // Convert to dates and remove duplicates
          const uniqueMonths = [
            ...new Set(invoiceList.map((inv) => inv.month)),
          ];

          // Sort months
          const sortedMonths = uniqueMonths.sort(
            (a, b) => new Date(a + "-01") - new Date(b + "-01"),
          );

          // Format months
          const formattedMonths = sortedMonths.map((monthStr) => {
            const date = new Date(monthStr + "-01");
            const month = date.toLocaleString("default", { month: "short" });
            const year = date.getFullYear();
            return `${month} ${year}`;
          });

          if (formattedMonths.length === 1) {
            return formattedMonths[0];
          } else if (formattedMonths.length === 2) {
            return formattedMonths.join(" and ");
          } else {
            return (
              formattedMonths.slice(0, -1).join(", ") +
              " and " +
              formattedMonths[formattedMonths.length - 1]
            );
          }
        };

        // Helper to calculate total
        const getTotal = (invoiceList) =>
          invoiceList.reduce((sum, inv) => sum + inv.balance, 0);

        // Separate invoices
        const monthlyInvoices = invoices.filter(
          (inv) => inv.type_text === "Monthly",
        );

        const annualInvoices = invoices.filter(
          (inv) => inv.type_text === "Annually",
        );

        // Generate month text
        const monthlyMonthsText = formatMonths(monthlyInvoices);
        const annualMonthsText = formatMonths(annualInvoices);

        // Totals
        const monthlyTotal = getTotal(monthlyInvoices);
        const annualTotal = getTotal(annualInvoices);

        // Monthly Message (Invoices)
        const monthlyMsg = monthlyMonthsText
          ? `Kindly clear your outstanding invoices for ${monthlyMonthsText} (Total: Rs. ${Intl.NumberFormat(
              "en-Us",
            ).format(
              monthlyTotal,
            )}) at your earliest convenience to avoid any disruption in services.`
          : "";

        // Annual Message (Web Hosting)
        const annualMsg = annualMonthsText
          ? `Your annual web hosting invoice for ${annualMonthsText} (Total: Rs. ${Intl.NumberFormat(
              "en-Us",
            ).format(
              annualTotal,
            )}) is overdue - please complete payment promptly to avoid possible service interruption or suspension.`
          : "";

        // Store in state
        setMonthlyMessage(monthlyMsg);
        setAnnualMessage(annualMsg);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fallbackTitle = location.pathname
    .split("/")
    .filter(Boolean)
    .pop()
    ?.replace(/-/g, " ")
    ?.replace(/\b\w/g, (letter) => letter.toUpperCase());
  const title = subheader.title || fallbackTitle || "Dashboard";
  const darkenHex = (hex, amount = 28) => {
    const normalized = hex.replace("#", "");
    if (normalized.length !== 6) return hex;
    const value = parseInt(normalized, 16);
    const r = Math.max(0, (value >> 16) - amount);
    const g = Math.max(0, ((value >> 8) & 0xff) - amount);
    const b = Math.max(0, (value & 0xff) - amount);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)}`;
  };
  const navBarDarkColor = darkenHex(config.navBarColor);

  return (
    <div
      id="kt_subheader"
      style={{
        "--subheader-theme-color": `${config.navBarColor}`,
        background: `linear-gradient(90deg, ${config.navBarColor} 0%, ${config.navBarColor} 58%, ${navBarDarkColor} 100%)`,
      }}
      className={`subheader modern-subheader py-2 py-lg-4 ${layoutProps.subheaderCssClasses}`}
    >
      <div
        className={`${layoutProps.subheaderContainerCssClasses} modern-subheader-container d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap`}
      >
        {/* Info */}
        <div className="modern-subheader-main d-flex align-items-center flex-wrap mr-1">
          {layoutProps.subheaderMobileToggle && (
            <button
              className="burger-icon burger-icon-left mr-4 d-inline-block d-lg-none"
              id="kt_subheader_mobile_toggle"
            >
              <span />
            </button>
          )}

          <div className="modern-subheader-title d-flex align-items-baseline mr-5">
            <h5
              className="font-weight-bold my-2 mr-5"
              style={{ color: `${config.navBarTxtColor}` }}
            >
              {title}
            </h5>
          </div>

          <BreadCrumbs items={subheader.breadcrumbs} />
        </div>

        {/* NavbarSlider */}
        {PremissionChecker(
          user,
          "campus_fee.can_view_invoice_payment_message",
        ) && (
          <NavbarSlider
            monthlyMessage={monthlyMessage}
            annualMessage={annualMessage}
          />
        )}
      </div>
    </div>
  );
}
